import { TooltipPosition } from './../shared/enums/tooltip-position.enum';
import { HttpMethods } from './../enums/http-method.enum';
import { Source } from '../enums/source.enum';
import { UiControlType } from '../enums/ui-control-type.enum';
import { DataSource } from '@angular/cdk/collections';

export interface UiConfig {
  config: Config[];
  sectionsOrder?: string[];
  displayType: ConfigDisplayType;
  sectionsInfo?: {
    [sectionName: string]: {
      displayLabel?: string;
      class?: string;
      tooltipConfig?: TooltipConfig;
      hideSection?: boolean;
    };
  };
  subSections: {
    [sectionName: string]: string[];
  };
  excludeSections?: string[];
  styles: string;
}
export interface SegmentationUiConfig {
  configs: {
    [configName: string]: UiConfig & { displayText: string };
  };
  tabsInfo: {
    [tabName: string]: {
      displayText: string;
      class?: string;
      tooltipConfig?: TooltipConfig;
    };
  };
  tabsOrder: string[];
  configOrderByTab: {
    [tabName: string]: string[];
  };
}
export interface TooltipConfig {
  tooltipDesc?: string;
  tooltipPos?: TooltipPosition;
  styles?: any;
  divStyles?: any;
  tooltipText?: string;
  tooltipClass?: string;
}

export enum ConstraintPositionType {
  Fixed = 'fixed',
  Relative = 'relative',
}
export enum ConstraintPosition {
  Before = 'before',
  After = 'after',
}
export interface ConstraintPositionConfig {
  positionType?: ConstraintPositionType;
  position?: ConstraintPosition;
  index?: number;
  relativeTo?: string;
}
export enum ConfigDisplayType {
  Accordion = 'accordion',
  Tab = 'tab',
  None = 'none',
}

export enum Alignment {
  Start = 'start',
  End = 'end',
}

export interface Config {
  property?: string;
  displayText?: string;
  uiControlType?: UiControlType;
  info?: string;
  source?: Source;
  sourceExpression?: string;
  dependentValues?: any;
  values?: any[];
  valuesFunction?: string;
  valuesUrl?: string;
  requestBody?: RequestBody;
  requestBodyStatic?: RequestBody;
  valueKeyPath?: string;
  fetchMethod?: HttpMethods;
  /** Boolean, or a `{{...}}` expression re-evaluated on form-value changes. */
  isMultiSelect?: boolean | string;
  /**
   * Width of the dropdown options overlay. Mirrors mat-select's `panelWidth`.
   * Defaults to `null` (overlay sizes to its widest option). Set to `'auto'`
   * to match the trigger width so the panel fills wide containers.
   */
  panelWidth?: string | number | null;
  /**
   * Extra CSS class(es) added to the opened options overlay (mat-select
   * `panelClass`), appended to the always-present `ra-dropdown-wide-panel
   * mat-select-panel`. Use it to scope panel styling to a single screen (e.g.
   * `pp-config-wide-panel`) without affecting every other dynamic-form dropdown.
   */
  panelClass?: string;
  /** Mirrors mat-select's `disableOptionCentering`; defaults to true for legacy dropdown positioning. */
  disableOptionCentering?: boolean;
  dropdownID?: string;
  dropdownLabel?: string;
  dropdownIdExpression?: RequestBody;
  dropdownLabelExpression?: string;
  validators?: UIControlValidators;
  dynamicValidators?: UIControlValidators;
  parentValidators?: UIControlValidators;
  errorMessages?: { [key: string]: string };
  parentErrorMessages?: { [key: string]: string };
  children?: Config[];
  control?: any;
  data_cy?: string;
  section?: string;
  isDependentControl?: boolean;
  conditionExpression?: string;
  isConditionSatisfied?: boolean;
  csvConfig?: CSVConfig;
  loading?: boolean;
  defaultValue?: any;
  valuesToAdd?: any;
  filterValues?: boolean;
  mapValueNames?: { [key: string]: string };
  dictValuesConfig?: {
    config?: Config[];
    valueDefault?: any;
    valueConfig?: Config;
  };
  dictKeyConfig?: Config;
  cascadeFromControl?: string;
  /** dynamicDict ``dictKeyConfig``: when set, the dict's keys are LOCKED to
   *  the keys of another control's dict value (path relative to the main
   *  form), rendered as fixed labels instead of an editable key picker. The
   *  child value FormControls remain editable. Used to keep an overlay's
   *  per-column value-only multi-selects synced with the current run's
   *  shared subset columns. */
  inheritKeysFrom?: string;
  /** dynamicDict ``dictValuesConfig.valueConfig``: absolute path to the
   *  current run's subset dict ({col:[values]}). When set, options offered
   *  for an overlay's value multi-select EXCLUDE values claimed by the
   *  current run's subset and by sibling overlay rows (single shared
   *  column case only). */
  excludeValuesClaimedBySiblings?: string;
  formValue?: any;
  class?: string;
  externalSelector?: string;
  reloadValues?: boolean;
  returnTypeString?: boolean;
  hide?: boolean;
  orderOnly?: boolean;
  order?: boolean;
  topDirectionText?: string;
  bottomDirectionText?: string;
  topDirectionColor?: string;
  bottomDirectionColor?: string;
  nextLine?: boolean;
  htmlOrText?: string;
  tooltipConfig?: TooltipConfig;
  selectAllOnInit?: boolean;
  avoidNone?: boolean;
  idExpressionUpdated?: boolean;
  labelExpressionUpdated?: boolean;
  resetOnValueChange?: {
    [pathToValue: string]: {
      conditionExpression?: string;
    };
  };
  path?: string;
  csvId?: string;
  sourceListDisplayText?: string;
  targetListDisplayText?: string;
  filteredValues?: any[];
  allOptions?: any[];
  optionsToShow?: any[];
  minNumOfOptionsToShow?: number;
  dependentConstraintsConditionExpression?: { [key: string]: string };
  constraintsToAddIfEnabled?: [{ [key: string]: ConstraintPositionConfig }];
  fileName?: string;
  specialValuesToAdd?: {
    [key: string]: any;
  };
  downloadURL?: string;
  readOnly?: boolean;
  viewData?: boolean;
  fileStatsURL?: string;
  /** When true, skip the initial 0-row file-stats check on load; still validate on change and when Run Model runs (price-band-setup). */
  deferInitialFileStatsValidation?: boolean;
  isGrouped?: boolean;
  /**
   * Option values that should render as disabled (visible but unselectable).
   * Backward-compatible: when unset/empty, every option stays selectable.
   * Compared against each option's resolved value (honoring `dropdownID` /
   * `optionDisplayFields`) via deep equality.
   */
  disabledValues?: any[];
  /**
   * Small muted note rendered beneath each disabled option (see
   * `disabledValues`), e.g. "Selected in Products — Hierarchy". Optional; when
   * unset, disabled options show no extra line.
   */
  disabledValueHint?: string;
  optionDisplayFields?: string[];
  defaultValueConditionExpression?: { [key: string]: string };
  readOnlyConditionExpression?: string;
  id?: string;
  acceptedFileType?: string;
  delimiter?: string;
  isColRegEx?: boolean;
  hasDefaultDictValues?: boolean;
  hintLabel?: string;
  /** When true, chip control shows a search field that filters options by label (selected chips stay visible). */
  enableChipOptionSearch?: boolean;
  /** Mat-label for chip search; set when `enableChipOptionSearch` is true. */
  chipOptionSearchLabel?: string;
  /** Input placeholder for chip search; set when `enableChipOptionSearch` is true. */
  chipOptionSearchPlaceholder?: string;
  hintAlignment?: Alignment;
  enableUniqueRows?: boolean;
  enableSelectAll?: boolean;
  resetDictValuesOnKeyChange?: boolean;
  mask?: string;
  thousandSeparator?: string;
  reorderable?: boolean;
}

export interface CSVConfig {
  data?: any;
  dataSource?: DataSource<any>;
  isLoading?: boolean;
  downloadURL?: string;
  uploadURL?: string;
  validateURL?: string;
  paramControls?: UiConfig;
  isEditable?: boolean;
  downloadMethod?: HttpMethods;
  uploadMethod?: HttpMethods;
  parentConfigParams?: Params;
  parentValueChangeReloadParams?: string[];
  fixedColumns?: string[];
  staticCols?: string[];
  dtypes?: { [column: string]: string };
  editColumns?: boolean;
  editRows?: boolean;
  columnControls?: { [column: string]: Config };
  uploadBtnText?: string;
  requestBody?: RequestBody;
  requestBodyStatic?: RequestBody;
  csvDataUploadKey?: string;
  info?: string;
  newColumnConfig?: NewColumnConfig;
  notSelectedError?: boolean;
  notSavedError?: boolean;
  isInputData?: boolean;
  invalidColumnsError?: boolean;
  skipValidation?: boolean;
  hide?: boolean;
  csvRowsDisclaimerMsg?: string;
  allowNullStaticCols?: { allow_nulls_in: string[]; strategy: string };
}

export interface NewColumnConfig {
  uiControlType: UiControlType;
  reloadValues: boolean;
  source: Source;
  valuesUrl: string;
  valuesKeyPath: string;
  fetchMethod: HttpMethods;
  valuesToAdd: any;
  filterValues: boolean;
  mapValueNames?: { [key: string]: string };
  requestBody: RequestBody;
  values?: any[];
  loading?: boolean;
  addAsPair?: boolean;
  pairedColPrefix?: string;
  pairedColSuffix?: string;
}

export interface RequestBody {
  [key: string]: any;
}
export interface UIControlValidators {
  [key: string]: any;
}

export interface Params {
  [key: string]: string;
}
export interface Body {
  [key: string]: any;
}
